import React, { createContext, useCallback, useContext, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { animate, motion, useMotionValue, useReducedMotion, useTransform, type AnimationPlaybackControls, type MotionValue } from 'framer-motion';
import { BookOpen, ExternalLink, FolderInput, Play, Share, Trash2, X } from 'lucide-react';
import { toast } from 'sonner';
import type { LibraryCard } from '@/lib/library/libraryCard';
import { sourceMeta, type LibrarySource } from '@/lib/library/sources';
import { SourceGlyph } from './SourceGlyph';
import { useLibrary } from './LibraryContext';
import { LibrarySpacePicker } from './LibrarySheets';
import { ItemComments } from './ItemComments';
import { SwipeCarousel } from './SwipeCarousel';
import { Avatar } from './LibraryTile';
import { PdfPagesCarousel } from './PdfPages';
import { PdfReaderDialog } from '@/components/items/PdfReader';
import { cn } from '@/lib/utils';
import { isEditableNote, noteText } from '@/lib/library/noteBody';
import { useKeyboardInset } from '@/hooks/useKeyboardInset';
import { CHROME_TOP } from './chromeMetrics';
import { NoteEditor } from './NoteEditor';

// Chrome presence -----------------------------------------------------------
// The shell hides the search pill and tab bar while a detail view is open.
// The grid reports through this context; MobileLibrary provides it.

const DetailPresenceContext = createContext<(open: boolean) => void>(() => {});

export function LibraryDetailPresenceProvider({ onChange, children }: { onChange: (open: boolean) => void; children: React.ReactNode }) {
  return <DetailPresenceContext.Provider value={onChange}>{children}</DetailPresenceContext.Provider>;
}

export function useLibraryDetailPresence(): (open: boolean) => void {
  return useContext(DetailPresenceContext);
}

// Detail view ---------------------------------------------------------------

export interface LibraryDetailProps {
  /** The list the tile came from: horizontal swipes page through it. */
  cards: LibraryCard[];
  initialId: string;
  /** The tile's picture on screen when tapped: the expand starts here. */
  originRect: DOMRect | null;
  /** The grid's tile radius at this density, so the flight lands on the same corners. */
  tileRadius: number;
  /** Where a card's tile picture is now (the grid scrolls to it if needed), or null. */
  getTileRect: (id: string) => DOMRect | null;
  /** The card on screen changed (paging): the grid hides that tile instead. */
  onCurrentChange: (id: string) => void;
  /** Closing started: the chrome can come back. */
  onClose: () => void;
  /** The card has landed on its tile: show the tile underneath, the card fades out over it. */
  onLand?: () => void;
  /** Closing finished: unmount. */
  onClosed: () => void;
}

const SPRING = { type: 'spring' as const, stiffness: 500, damping: 38 };
/** The flight home counts as landed a little before the spring's long tail ends. */
const LAND_SPRING = { ...SPRING, restDelta: 0.5, restSpeed: 30 };
/** Space between cards while paging, like a photo viewer. */
const PAGE_GAP = 24;
/** How long the card takes to fade into its tile once it lands. */
const LAND_FADE = 0.14;
const EASE_OUT = [0.22, 1, 0.36, 1] as const;
const DETAIL_RADIUS = 14;
/** Past this pull (px) or speed (px/ms) a swipe down dismisses. */
const DISMISS_DISTANCE = 110;
const DISMISS_SPEED = 0.45;
const PAGE_SPEED = 0.45;

type Phase = 'opening' | 'open' | 'closing';

const OPEN_LABELS: Partial<Record<LibrarySource, string>> = {
  instagram_reel: 'Open in Instagram',
  instagram_post: 'Open in Instagram',
  tiktok: 'Open in TikTok',
  x: 'Open in X',
  youtube: 'Open in YouTube',
  pinterest: 'Open in Pinterest',
  threads: 'Open in Threads',
  linkedin: 'Open in LinkedIn',
  facebook: 'Open in Facebook',
  reddit: 'Open in Reddit',
  vimeo: 'Open in Vimeo',
};

function str(v: unknown): string {
  return typeof v === 'string' ? v : '';
}

function originalUrl(card: LibraryCard): string | null {
  if (card.url) return card.url;
  const c = (card.item.content ?? {}) as Record<string, unknown>;
  if (card.item.item_type === 'file' && str(c.url)) return str(c.url);
  return null;
}

function videoUrl(card: LibraryCard): string | null {
  if (card.item.item_type !== 'video') return null;
  const c = (card.item.content ?? {}) as Record<string, unknown>;
  return str(c.videoUrl) || str(c.url) || null;
}

function youTubeId(url: string | null): string | null {
  if (!url) return null;
  try {
    const u = new URL(url);
    if (u.hostname.includes('youtu.be')) return u.pathname.slice(1) || null;
    if (u.hostname.includes('youtube.com')) {
      if (u.pathname.startsWith('/shorts/') || u.pathname.startsWith('/embed/')) return u.pathname.split('/')[2] || null;
      return u.searchParams.get('v');
    }
  } catch {
    // not a URL
  }
  return null;
}

function formatDate(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const sameYear = d.getFullYear() === new Date().getFullYear();
  return d.toLocaleDateString(undefined, { day: 'numeric', month: 'short', ...(sameYear ? {} : { year: 'numeric' }) });
}

function isTextCard(card: LibraryCard): boolean {
  if (card.tile === 'pdf' && card.fileUrl) return false;
  return !card.thumbnail && !videoUrl(card);
}

function compact(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1).replace(/\.0$/, '')}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1).replace(/\.0$/, '')}K`;
  return String(n);
}

/**
 * Full-screen view of one card, opened from a grid tile. The tile's picture
 * expands into a fitted rect (shared element) while paper fades in behind
 * it. Swipe down to dismiss (the picture follows the finger and shrinks,
 * then flies back into its tile), swipe sideways for the previous or next
 * card. Escape and the browser back gesture close it.
 */
export function LibraryDetail({ cards, initialId, originRect, tileRadius, getTileRect, onCurrentChange, onClose, onLand, onClosed }: LibraryDetailProps) {
  const reduce = useReducedMotion() ?? false;
  const { actions } = useLibrary();
  const [currentId, setCurrentId] = useState(initialId);
  const [phase, setPhase] = useState<Phase>('opening');
  const [moveOpen, setMoveOpen] = useState(false);
  // The PDF reader opens over the view; Escape and swipes belong to it while it is up
  const [readerOpen, setReaderOpen] = useState(false);
  const [readerPage, setReaderPage] = useState(1);
  const [playing, setPlaying] = useState(false);
  const [lastCard, setLastCard] = useState<LibraryCard | null>(null);
  // Typing in the note: swipes, arrow-key paging and Escape-to-close stand down
  const [editing, setEditing] = useState(false);
  const editingRef = useRef(false);
  const setEditingBoth = useCallback((next: boolean) => {
    editingRef.current = next;
    setEditing(next);
  }, []);
  const keyboardInset = useKeyboardInset();

  const found = useMemo(() => cards.find((c) => c.id === currentId) ?? null, [cards, currentId]);
  const card = found ?? lastCard;
  const index = found ? cards.indexOf(found) : -1;

  useEffect(() => {
    if (found) setLastCard(found);
  }, [found]);

  // Warm the pictures two cards either way, so paging never waits on the network
  useEffect(() => {
    const i = cards.findIndex((c) => c.id === currentId);
    if (i < 0) return;
    for (const c of [cards[i - 2], cards[i - 1], cards[i + 1], cards[i + 2]]) {
      if (!c?.thumbnail) continue;
      const img = new Image();
      img.decoding = 'async';
      img.src = c.thumbnail;
    }
  }, [cards, currentId]);

  const rootRef = useRef<HTMLDivElement>(null);
  const scrollerRef = useRef<HTMLDivElement>(null);
  const boxRef = useRef<HTMLDivElement>(null);
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  const phaseRef = useRef<Phase>('opening');
  const running = useRef<AnimationPlaybackControls[]>([]);
  const ignorePopRef = useRef(false);

  const rootOpacity = useMotionValue(1);
  const backdrop = useMotionValue(1);
  const info = useMotionValue(1);
  const pageX = useMotionValue(0);
  const pageOpacity = useMotionValue(1);
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const ms = useMotionValue(1);
  const radius = useMotionValue(DETAIL_RADIUS);
  // Text cards: the visible height while flying, so a long note never spills
  // past its tile (-1 = natural height)
  const textH = useMotionValue(-1);
  const textHeight = useTransform(textH, (v) => (v < 0 ? 'auto' : `${v}px`));

  /** Bumped whenever animations are stopped, so a stopped step can't finish late. */
  const generation = useRef(0);
  const stopAll = useCallback(() => {
    generation.current += 1;
    running.current.forEach((a) => a.stop());
    running.current = [];
  }, []);

  const run = useCallback((value: MotionValue<number>, to: number, options: Parameters<typeof animate>[2]) => {
    const controls = animate(value, to, options as never);
    running.current.push(controls);
    return controls;
  }, []);

  const currentIdRef = useRef(currentId);
  const cardsRef = useRef(cards);
  useEffect(() => {
    currentIdRef.current = currentId;
    cardsRef.current = cards;
  }, [currentId, cards]);

  const setPhaseBoth = useCallback((next: Phase) => {
    phaseRef.current = next;
    setPhase(next);
  }, []);

  // Open: start the picture on the tile's rect, then spring it home.
  useLayoutEffect(() => {
    const box = boxRef.current?.getBoundingClientRect();
    if (reduce || !originRect || !box || box.width === 0) {
      rootOpacity.jump(0);
      run(rootOpacity, 1, { duration: 0.2, ease: EASE_OUT }).then(() => setPhaseBoth('open'));
      return;
    }
    const k = originRect.width / box.width;
    mx.jump(originRect.left - box.left);
    my.jump(originRect.top - box.top);
    ms.jump(k);
    radius.jump(tileRadius / k);
    backdrop.jump(0);
    info.jump(0);
    // A note grows from its tile's height to its full length
    const opened = cardsRef.current.find((c) => c.id === initialId);
    if (opened && isTextCard(opened)) {
      textH.jump(originRect.height / k);
      run(textH, box.height, SPRING).then(() => textH.jump(-1));
    }
    run(backdrop, 1, { duration: 0.24, ease: EASE_OUT });
    run(info, 1, { duration: 0.2, delay: 0.1, ease: EASE_OUT });
    run(radius, DETAIL_RADIUS, SPRING);
    run(mx, 0, SPRING);
    run(my, 0, SPRING);
    run(ms, 1, SPRING).then(() => {
      if (phaseRef.current === 'opening') setPhaseBoth('open');
    });
    // Mount-only: the open animation runs once from the tapped tile
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    closeButtonRef.current?.focus({ preventScroll: true });
  }, []);


  const close = useCallback(
    (velocity?: { x: number; y: number }) => {
      if (phaseRef.current === 'closing') return;
      // A note (or comment) being typed: blur it first, which saves the note
      // and puts the keyboard away before the flight home
      const active = document.activeElement as HTMLElement | null;
      if (active && active !== document.body && rootRef.current?.contains(active)) active.blur();
      setPhaseBoth('closing');
      stopAll();
      setMoveOpen(false);
      setPlaying(false);
      onClose();
      if (window.history.state?.libraryDetail) {
        ignorePopRef.current = true;
        window.history.back();
      }

      const target = getTileRect(currentIdRef.current);
      const box = boxRef.current?.getBoundingClientRect();
      if (reduce || !target || !box || box.width === 0) {
        onLand?.();
        run(rootOpacity, 0, { duration: 0.18, ease: EASE_OUT }).then(onClosed);
        return;
      }
      // The box's untransformed rect: the page may still be offset by a swipe
      const left = box.left - pageX.get();
      const top = box.top;
      const k = target.width / box.width;
      const current = cardsRef.current.find((c) => c.id === currentIdRef.current);
      run(pageX, 0, SPRING);
      run(backdrop, 0, { duration: 0.22, ease: EASE_OUT });
      run(info, 0, { duration: 0.12, ease: EASE_OUT });
      run(radius, tileRadius / k, SPRING);
      run(ms, k, SPRING);
      // A long note shortens to its tile's height on the way, instead of spilling over the grid
      if (current && isTextCard(current)) run(textH, target.height / k, SPRING);
      run(mx, target.left - left, { ...LAND_SPRING, velocity: (velocity?.x ?? 0) * 1000 });
      const gen = generation.current;
      run(my, target.top - top, { ...LAND_SPRING, velocity: (velocity?.y ?? 0) * 1000 }).then(() => {
        if (gen !== generation.current) return;
        // Landed: the tile appears underneath and the card fades into it, so its
        // picture, badges and corners hand over without a pop
        onLand?.();
        run(rootOpacity, 0, { duration: LAND_FADE, ease: EASE_OUT }).then(onClosed);
      });
    },
    [backdrop, getTileRect, info, ms, mx, my, onClose, onClosed, onLand, pageX, radius, reduce, rootOpacity, run, setPhaseBoth, stopAll, textH, tileRadius],
  );

  const closeRef = useRef(close);
  useEffect(() => {
    closeRef.current = close;
  }, [close]);

  const resetMedia = useCallback(() => {
    mx.jump(0);
    my.jump(0);
    ms.jump(1);
    radius.jump(DETAIL_RADIUS);
    textH.jump(-1);
  }, [ms, mx, my, radius, textH]);

  /** Page to the previous (-1) or next (+1) card. */
  const go = useCallback(
    (dir: 1 | -1, velocity = 0) => {
      if (phaseRef.current !== 'open') return;
      const list = cardsRef.current;
      const i = list.findIndex((c) => c.id === currentIdRef.current);
      const next = list[i + dir];
      if (!next) {
        run(pageX, 0, SPRING);
        return;
      }
      stopAll();
      const gen = generation.current;
      const vw = window.innerWidth;
      const swap = () => {
        setCurrentId(next.id);
        currentIdRef.current = next.id;
        onCurrentChange(next.id);
        setPlaying(false);
        if (scrollerRef.current) scrollerRef.current.scrollTop = 0;
        resetMedia();
        backdrop.jump(1);
        info.jump(1);
      };
      if (reduce) {
        run(pageOpacity, 0, { duration: 0.1, ease: EASE_OUT }).then(() => {
          if (gen !== generation.current) return;
          swap();
          run(pageOpacity, 1, { duration: 0.16, ease: EASE_OUT });
        });
        return;
      }
      // The neighbour is already drawn beside this card: slide until it is
      // centred, then make it the current card where it stands
      run(pageX, -dir * (vw + PAGE_GAP), { ...LAND_SPRING, velocity: velocity * 1000 }).then(() => {
        if (gen !== generation.current) return;
        swap();
        pageX.jump(0);
      });
    },
    [backdrop, info, onCurrentChange, pageOpacity, pageX, reduce, resetMedia, run, stopAll],
  );

  const moveOpenRef = useRef(moveOpen);
  useEffect(() => {
    moveOpenRef.current = moveOpen || readerOpen;
  }, [moveOpen, readerOpen]);

  const goRef = useRef(go);
  useEffect(() => {
    goRef.current = go;
  }, [go]);

  // History entry: the back gesture closes the view instead of leaving the page
  useEffect(() => {
    if (!window.history.state?.libraryDetail) {
      window.history.pushState({ ...(window.history.state ?? {}), libraryDetail: true }, '');
    }
    const onPop = () => {
      if (ignorePopRef.current) {
        ignorePopRef.current = false;
        return;
      }
      closeRef.current();
    };
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);

  // Keyboard: Escape closes, arrows page
  useEffect(() => {
    // Capture phase, so an open space underneath never sees this Escape
    const onKey = (e: KeyboardEvent) => {
      if (e.defaultPrevented || moveOpenRef.current) return; // the move sheet handles its own keys
      if (e.key === 'Escape') {
        e.preventDefault();
        e.stopImmediatePropagation();
        // Escape while typing a note ends the edit; the next one closes
        if (editingRef.current) (document.activeElement as HTMLElement | null)?.blur();
        else closeRef.current();
        return;
      }
      // Arrows in a note or the comment box move the caret, not the card
      const target = e.target as HTMLElement | null;
      if (target?.closest?.('input, textarea, [contenteditable="true"]')) return;
      if (e.key === 'ArrowRight') goRef.current(1);
      else if (e.key === 'ArrowLeft') goRef.current(-1);
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, []);

  // Touch: swipe down to dismiss, sideways to page. Vertical scrolling of a
  // long note stays native; we only take over from the top of the scroll.
  useEffect(() => {
    const el = scrollerRef.current;
    if (!el) return;
    type Mode = 'pending' | 'dismiss' | 'page' | 'native';
    let mode: Mode = 'native';
    // A horizontal swipe that starts on a carousel turns its slides, not the card
    let onCarousel = false;
    let start = { x: 0, y: 0 };
    let grab = { x: 0, y: 0 };
    let last = { x: 0, y: 0, t: 0 };
    let velocity = { x: 0, y: 0 };
    let startScroll = 0;
    let dx = 0;
    let dy = 0;

    const onStart = (e: TouchEvent) => {
      if (e.touches.length !== 1 || phaseRef.current !== 'open' || moveOpenRef.current || editingRef.current) {
        mode = 'native';
        return;
      }
      const t = e.touches[0];
      if ((t.target as HTMLElement | null)?.closest?.('iframe, [data-detail-no-swipe]')) {
        mode = 'native';
        return;
      }
      mode = 'pending';
      onCarousel = !!(t.target as HTMLElement | null)?.closest?.('[data-detail-carousel]');
      start = { x: t.clientX, y: t.clientY };
      last = { x: t.clientX, y: t.clientY, t: performance.now() };
      velocity = { x: 0, y: 0 };
      startScroll = el.scrollTop;
      const box = boxRef.current?.getBoundingClientRect();
      grab = box ? { x: t.clientX - box.left, y: t.clientY - box.top } : { x: 0, y: 0 };
      dx = 0;
      dy = 0;
    };

    const onMove = (e: TouchEvent) => {
      if (mode === 'native') return;
      const t = e.touches[0];
      if (!t) return;
      dx = t.clientX - start.x;
      dy = t.clientY - start.y;
      const now = performance.now();
      const dt = Math.max(1, now - last.t);
      velocity = { x: 0.7 * ((t.clientX - last.x) / dt) + 0.3 * velocity.x, y: 0.7 * ((t.clientY - last.y) / dt) + 0.3 * velocity.y };
      last = { x: t.clientX, y: t.clientY, t: now };

      if (mode === 'pending') {
        if (Math.hypot(dx, dy) < 8) return;
        if (Math.abs(dy) > Math.abs(dx) && dy > 0 && startScroll <= 0) mode = 'dismiss';
        else if (Math.abs(dx) > Math.abs(dy) * 1.2) mode = onCarousel ? 'native' : 'page';
        else {
          mode = 'native';
          return;
        }
        stopAll();
      }
      if (e.cancelable) e.preventDefault();

      if (mode === 'dismiss') {
        const vh = window.innerHeight;
        const pull = Math.max(0, dy);
        const p = Math.min(1, pull / (vh * 0.5));
        const s = 1 - 0.4 * p;
        const y = dy >= 0 ? dy : dy * 0.3;
        mx.set(dx + grab.x * (1 - s));
        my.set(y + grab.y * (1 - s));
        ms.set(s);
        backdrop.set(1 - p);
        info.set(Math.max(0, 1 - p * 3));
      } else if (mode === 'page') {
        const list = cardsRef.current;
        const i = list.findIndex((c) => c.id === currentIdRef.current);
        const blocked = (dx > 0 && i <= 0) || (dx < 0 && i >= list.length - 1);
        pageX.set(blocked ? dx * 0.3 : dx);
      }
    };

    const onEnd = () => {
      const m = mode;
      mode = 'native';
      if (m === 'dismiss') {
        if (dy > DISMISS_DISTANCE || (velocity.y > DISMISS_SPEED && dy > 24)) {
          closeRef.current(velocity);
        } else {
          run(mx, 0, SPRING);
          run(my, 0, SPRING);
          run(ms, 1, SPRING);
          run(backdrop, 1, { duration: 0.2, ease: EASE_OUT });
          run(info, 1, { duration: 0.2, ease: EASE_OUT });
        }
      } else if (m === 'page') {
        const vw = window.innerWidth;
        const fast = Math.abs(velocity.x) > PAGE_SPEED && Math.sign(velocity.x) === Math.sign(dx);
        if (Math.abs(dx) > vw * 0.22 || fast) goRef.current(dx < 0 ? 1 : -1, velocity.x);
        else run(pageX, 0, SPRING);
      }
    };

    const blockGesture = (e: Event) => e.preventDefault();
    el.addEventListener('touchstart', onStart, { passive: true });
    el.addEventListener('touchmove', onMove, { passive: false });
    el.addEventListener('touchend', onEnd);
    el.addEventListener('touchcancel', onEnd);
    el.addEventListener('gesturestart', blockGesture, { passive: false } as AddEventListenerOptions);
    return () => {
      el.removeEventListener('touchstart', onStart);
      el.removeEventListener('touchmove', onMove);
      el.removeEventListener('touchend', onEnd);
      el.removeEventListener('touchcancel', onEnd);
      el.removeEventListener('gesturestart', blockGesture);
    };
  }, [backdrop, info, ms, mx, my, pageX, run, stopAll]);

  useEffect(() => () => stopAll(), [stopAll]);

  if (!card) return null;

  const meta = sourceMeta(card.source);
  const url = originalUrl(card);
  const prev = index > 0 ? cards[index - 1] : null;
  const next = index >= 0 && index < cards.length - 1 ? cards[index + 1] : null;

  const share = async () => {
    const text = noteText(card.item) || card.text || card.title;
    try {
      if (typeof navigator.share === 'function') {
        await navigator.share(url ? { title: card.title || undefined, url } : { text });
        return;
      }
    } catch (err) {
      if ((err as DOMException)?.name === 'AbortError') return;
    }
    try {
      await navigator.clipboard.writeText(url ?? text);
      toast(url ? 'Copied the link' : 'Copied the text');
    } catch {
      toast.error('Could not share this');
    }
  };

  const remove = async () => {
    const deleted = await actions.deleteItem(card.item);
    if (deleted) close();
  };


  return createPortal(
    <motion.div
      ref={rootRef}
      role="dialog"
      aria-modal="true"
      data-state={phase === 'closing' ? 'closed' : 'open'}
      aria-label={card.title || meta.noun}
      className="fixed inset-0 z-50 overflow-hidden text-ink"
      style={{ opacity: rootOpacity, pointerEvents: phase === 'closing' ? 'none' : undefined }}
    >
      <motion.div className="absolute inset-0 bg-paper" style={{ opacity: backdrop }} />

      <div ref={scrollerRef} className="absolute inset-0 overflow-y-auto overflow-x-hidden overscroll-contain" style={{ touchAction: 'pan-y' }}>
        <motion.div className="relative min-h-full" style={{ x: pageX, opacity: pageOpacity }}>
          {/* The neighbours are drawn beside the card, so a swipe moves real cards and they arrive loaded */}
          {prev && (
            <div aria-hidden className="pointer-events-none absolute inset-x-0 top-0" style={{ transform: `translateX(calc(-100% - ${PAGE_GAP}px))` }}>
              <DetailPage card={prev} phase="open" />
            </div>
          )}
          <DetailPage
            card={card}
            current
            phase={phase}
            boxRef={boxRef}
            media={{ x: mx, y: my, scale: ms, radius, textHeight }}
            info={info}
            playing={playing}
            onPlay={() => setPlaying(true)}
            keyboardInset={keyboardInset}
            onEditingChange={setEditingBoth}
            onSaveNote={(body) => void actions.updateNote(card.item, body)}
            actions={{
              read:
                card.tile === 'pdf' && card.fileUrl
                  ? (page = 1) => {
                      setReaderPage(page);
                      setReaderOpen(true);
                    }
                  : null,
              open: url ? () => window.open(url, '_blank', 'noopener,noreferrer') : null,
              move: () => setMoveOpen(true),
              share,
              remove,
            }}
          />
          {next && (
            <div aria-hidden className="pointer-events-none absolute inset-x-0 top-0" style={{ transform: `translateX(calc(100% + ${PAGE_GAP}px))` }}>
              <DetailPage card={next} phase="open" />
            </div>
          )}
        </motion.div>
      </div>

      <motion.div
        className="pointer-events-none absolute inset-x-0 top-0 flex h-14 items-center justify-between px-2"
        style={{ opacity: info, marginTop: CHROME_TOP }}
      >
        <button
          ref={closeButtonRef}
          type="button"
          onClick={() => close()}
          aria-label="Close"
          className="pointer-events-auto flex h-10 w-10 items-center justify-center rounded-full bg-paper-raised/90 text-ink-2 shadow-sm outline-none active:bg-wash focus-visible:ring-2 focus-visible:ring-signal"
        >
          <X className="h-5 w-5" strokeWidth={1.75} />
        </button>
        {editing ? (
          // Return types a new line in a note, so this is how the keyboard goes away
          <button
            type="button"
            onClick={() => (document.activeElement as HTMLElement | null)?.blur()}
            className="pointer-events-auto mr-1 flex h-9 items-center rounded-full bg-ink px-4 text-[14px] font-medium text-paper shadow-sm outline-none transition-transform duration-300 ease-pop active:scale-[0.96] active:duration-100 focus-visible:ring-2 focus-visible:ring-signal motion-reduce:transition-none motion-reduce:active:scale-100"
          >
            Done
          </button>
        ) : (
          index >= 0 &&
          cards.length > 1 && (
            <span className="pr-3 font-mono text-[11px] text-ink-3" aria-live="polite">
              {index + 1} of {cards.length}
            </span>
          )
        )}
      </motion.div>

      <LibrarySpacePicker card={card} open={moveOpen} onOpenChange={setMoveOpen} />
      {card.tile === 'pdf' && card.fileUrl && (
        <PdfReaderDialog open={readerOpen} onOpenChange={setReaderOpen} url={card.fileUrl} title={card.title || 'PDF'} initialPage={readerPage} />
      )}
    </motion.div>,
    document.body,
  );
}

interface DetailPageMedia {
  x: MotionValue<number>;
  y: MotionValue<number>;
  scale: MotionValue<number>;
  radius: MotionValue<number>;
  textHeight: MotionValue<string>;
}

interface DetailPageActions {
  /** Opens the reader, at `page` when a page was tapped. */
  read?: ((page?: number) => void) | null;
  open: (() => void) | null;
  move: () => void;
  share: () => void;
  remove: () => void;
}

/**
 * One card's page: the picture (or the note) and, under it, the source pill,
 * title, text and actions. The current page carries the motion values the
 * open, dismiss and paging gestures drive; the neighbours beside it render
 * the same page at rest, so paging hands over without a change.
 */
function DetailPage({
  card,
  current = false,
  phase,
  boxRef,
  media,
  info,
  playing = false,
  onPlay,
  keyboardInset = 0,
  onEditingChange,
  onSaveNote,
  actions,
}: {
  card: LibraryCard;
  current?: boolean;
  phase: Phase;
  boxRef?: React.Ref<HTMLDivElement>;
  media?: DetailPageMedia;
  info?: MotionValue<number>;
  playing?: boolean;
  onPlay?: () => void;
  keyboardInset?: number;
  onEditingChange?: (editing: boolean) => void;
  onSaveNote?: (body: string) => void;
  actions?: DetailPageActions;
}) {
  const meta = sourceMeta(card.source);
  const url = originalUrl(card);
  const video = videoUrl(card);
  const ytId = card.source === 'youtube' ? youTubeId(card.url) : null;
  const textCard = isTextCard(card);
  const editableNote = textCard && isEditableNote(card.item);
  // The whole note: the tile's text stops at 600 characters
  const fullText = textCard ? noteText(card.item) : null;
  const date = formatDate(card.createdAt);
  const showTitle = !!card.title && !(textCard && card.text.trim().startsWith(card.title));
  const pdf = card.tile === 'pdf' && !!card.fileUrl;
  // The media's own shape: a tweet tile's ratio includes its text, its photo's does not
  const ratio = pdf ? card.aspectRatio : card.mediaRatio;
  // Posts and tweets read as they do where they came from: who posted, then the whole caption
  // (a tweet that was only its photo still says who posted it)
  const rich = !textCard && (!!card.caption || card.tile === 'tweet');

  return (
    <div className="px-4" style={{ paddingTop: 'calc(68px + env(safe-area-inset-top))', paddingBottom: 'calc(40px + env(safe-area-inset-bottom))' }}>
      <div
        ref={boxRef}
        className="relative mx-auto"
        style={textCard ? { width: '100%' } : { width: `min(100%, calc(64vh * ${ratio}))`, aspectRatio: String(ratio) }}
      >
        <motion.div
          className={cn(
            'overflow-hidden border border-line',
            current && 'will-change-transform',
            textCard ? 'relative bg-paper-raised' : 'absolute inset-0 bg-paper-sunken',
          )}
          style={
            media
              ? { x: media.x, y: media.y, scale: media.scale, borderRadius: media.radius, originX: 0, originY: 0, ...(textCard ? { height: media.textHeight } : {}) }
              : { borderRadius: DETAIL_RADIUS }
          }
        >
          {textCard ? (
            <div className="p-5">
              {card.tile === 'tweet' && card.author && <AuthorRow author={card.author} className="mb-3" />}
              {editableNote ? (
                // A tap puts the caret in; a drag still dismisses or pages until then (the view stands
                // its swipes down while typing). The neighbours draw the same editor read-only, so
                // paging hands over without a change.
                <NoteEditor
                  key={card.id}
                  item={card.item}
                  editable={current && phase === 'open'}
                  keyboardInset={current ? keyboardInset : 0}
                  onEditingChange={current ? onEditingChange : undefined}
                  onSave={current ? onSaveNote : undefined}
                />
              ) : (
                <p className="whitespace-pre-line [overflow-wrap:anywhere] text-[16px] leading-[25px] text-ink">{fullText || card.text || card.title}</p>
              )}
            </div>
          ) : pdf ? (
            // Swipe through the pages here; a tap on one opens the reader on it. The page behind stays a still cover.
            current ? (
              <PdfPagesCarousel url={card.fileUrl!} label={card.title || 'PDF'} onOpenPage={(page) => actions?.read?.(page)} />
            ) : (
              <PdfPagesCarousel url={card.fileUrl!} label={card.title || 'PDF'} />
            )
          ) : current && card.media.length > 1 ? (
            <SwipeCarousel media={card.media} label={card.title || meta.noun} />
          ) : (
            <DetailMedia
              card={card}
              phase={current ? phase : 'opening'}
              video={video}
              ytId={ytId}
              playing={playing}
              onPlay={onPlay ?? (() => {})}
              openUrl={url}
              overlayOpacity={info}
              eager={!current}
            />
          )}
        </motion.div>
      </div>

      <motion.div className="mx-auto max-w-[560px] pt-5" style={info ? { opacity: info } : undefined}>
        <span className="inline-flex h-7 items-center gap-1.5 rounded-full border border-line bg-paper-raised px-2.5 text-[12px] text-ink-2">
          <SourceGlyph source={card.source} className="h-3.5 w-3.5" />
          <span>{meta.noun}</span>
          {date && (
            <>
              <span className="text-ink-3" aria-hidden>
                ·
              </span>
              <time dateTime={card.createdAt} className="text-ink-3">
                {date}
              </time>
            </>
          )}
        </span>

        {rich ? (
          <>
            {card.author && <AuthorRow author={card.author} className="mt-4" />}
            {card.caption && <p className="mt-3 whitespace-pre-line [overflow-wrap:anywhere] text-[16px] leading-[25px] text-ink">{card.caption}</p>}
          </>
        ) : (
          <>
            {showTitle && <h2 className="mt-3 font-display text-[22px] leading-[28px] text-ink [overflow-wrap:anywhere]">{card.title}</h2>}
            {card.subtitle && card.subtitle !== card.title && <p className="mt-1 text-[14px] leading-[20px] text-ink-3 [overflow-wrap:anywhere]">{card.subtitle}</p>}
            {!textCard && card.text && card.text.trim() !== card.title.trim() && (
              <p className="mt-3 whitespace-pre-line [overflow-wrap:anywhere] text-[15px] leading-[23px] text-ink-2">{card.text}</p>
            )}
          </>
        )}
        {card.stats && (card.stats.replies || card.stats.reposts || card.stats.likes || card.stats.views) ? (
          <p className="mt-3 flex flex-wrap gap-x-4 gap-y-1 font-mono text-[12px] text-ink-3">
            {card.stats.replies > 0 && <span>{compact(card.stats.replies)} replies</span>}
            {card.stats.reposts > 0 && <span>{compact(card.stats.reposts)} reposts</span>}
            {card.stats.likes > 0 && <span>{compact(card.stats.likes)} likes</span>}
            {card.stats.views > 0 && <span>{compact(card.stats.views)} views</span>}
          </p>
        ) : null}

        <div className="mt-6 flex gap-2">
          {pdf && <ActionButton icon={BookOpen} label="Read" onClick={actions?.read ? () => actions.read?.() : undefined} />}
          {url && <ActionButton icon={ExternalLink} label={OPEN_LABELS[card.source] ?? 'Open original'} onClick={actions?.open ?? undefined} />}
          <ActionButton icon={FolderInput} label="Move to space" onClick={actions?.move} />
          <ActionButton icon={Share} label="Share" onClick={actions?.share} />
          <ActionButton icon={Trash2} label="Delete" onClick={actions?.remove} />
        </div>

        {/* The thread lives with the item; typing here must not start a swipe */}
        {current && (
          <section data-detail-no-swipe className="mt-8">
            <h3 className="mb-3 px-1 font-mono text-[11px] uppercase tracking-[0.08em] text-ink-3">Comments</h3>
            <ItemComments itemId={card.id} />
          </section>
        )}
      </motion.div>
    </div>
  );
}

/** Who posted it: face, name and handle, as on the post itself. */
function AuthorRow({ author, className }: { author: NonNullable<LibraryCard['author']>; className?: string }) {
  const name = author.name || (author.handle ? `@${author.handle}` : '');
  return (
    <div className={cn('flex min-w-0 items-center gap-2.5', className)}>
      <Avatar src={author.avatar} name={name || '?'} size={32} />
      <div className="min-w-0 leading-[18px]">
        <p className="truncate text-[14px] font-medium text-ink">{name}</p>
        {author.handle && author.name && <p className="truncate text-[13px] text-ink-3">@{author.handle}</p>}
      </div>
    </div>
  );
}

function ActionButton({ icon: Icon, label, onClick }: { icon: typeof Share; label: string; onClick?: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="flex min-w-0 flex-1 flex-col items-center gap-1.5 rounded-[12px] border border-line bg-paper-raised px-1 py-3 text-[12px] leading-[15px] text-ink-2 outline-none transition-transform duration-300 ease-pop active:scale-[0.97] active:bg-wash active:duration-100 active:ease-out focus-visible:ring-2 focus-visible:ring-signal motion-reduce:transition-none motion-reduce:active:scale-100"
    >
      <Icon className="h-5 w-5 text-ink" strokeWidth={1.75} aria-hidden />
      <span className="line-clamp-2 w-full text-balance break-words text-center">{label}</span>
    </button>
  );
}

function DetailMedia({
  card,
  phase,
  video,
  ytId,
  playing,
  onPlay,
  openUrl,
  overlayOpacity,
  eager = false,
}: {
  card: LibraryCard;
  phase: Phase;
  video: string | null;
  ytId: string | null;
  playing: boolean;
  onPlay: () => void;
  openUrl: string | null;
  /** Fades the play button and open pill with the page's text, so they leave before the flight home. */
  overlayOpacity?: MotionValue<number>;
  /** Neighbour pages load their picture right away, so it is ready when they slide in. */
  eager?: boolean;
}) {
  const settled = phase === 'open';
  const label = OPEN_LABELS[card.source];
  return (
    <>
      {card.thumbnail && (
        <img src={card.thumbnail} alt="" decoding="async" loading={eager ? 'eager' : undefined} draggable={false} className="absolute inset-0 h-full w-full object-cover" />
      )}

      {video && settled && (
        <video
          src={video}
          poster={card.thumbnail ?? undefined}
          controls
          playsInline
          preload="metadata"
          data-detail-no-swipe
          className="absolute inset-0 h-full w-full bg-paper-sunken object-contain"
        />
      )}

      {ytId && settled && playing && (
        <iframe
          src={`https://www.youtube-nocookie.com/embed/${ytId}?autoplay=1&playsinline=1&rel=0`}
          title={card.title || 'YouTube video'}
          allow="autoplay; encrypted-media; picture-in-picture; fullscreen"
          allowFullScreen
          className="absolute inset-0 h-full w-full border-0"
        />
      )}

      {ytId && !playing && (
        <motion.button
          style={overlayOpacity ? { opacity: overlayOpacity } : undefined}
          type="button"
          onClick={onPlay}
          aria-label="Play video"
          className="absolute left-1/2 top-1/2 flex h-14 w-14 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full bg-paper-raised/90 text-ink shadow-sm outline-none focus-visible:ring-2 focus-visible:ring-signal"
        >
          <Play className="ml-0.5 h-6 w-6" fill="currentColor" strokeWidth={0} />
        </motion.button>
      )}

      {!ytId && !video && card.isVideo && openUrl && label && (
        <motion.a
          style={overlayOpacity ? { opacity: overlayOpacity } : undefined}
          href={openUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="absolute bottom-3 left-1/2 flex h-10 -translate-x-1/2 items-center gap-2 whitespace-nowrap rounded-full bg-paper-raised/95 px-4 text-[14px] font-medium text-ink shadow-sm outline-none focus-visible:ring-2 focus-visible:ring-signal"
        >
          <Play className="h-3.5 w-3.5" fill="currentColor" strokeWidth={0} aria-hidden />
          {label}
        </motion.a>
      )}
    </>
  );
}
